import { type ClassName, tw, twx } from '@ctw/shared/utils/tailwind';
import type { PropsWithChildren } from 'react';

const LABEL_CONTAINER_CLASS_NAME = tw`max-h-min pl-1 leading-0`;
const LABEL_CLASS_NAME = tw`font-label leading-tight tracking-wider`;
const REQUIRED_MARK_CLASS_NAME = tw`font-heading font-medium text-2xl text-error-text leading-none`;
const HELP_TEXT_CLASS_NAME = tw`font-medium text-content-subtle text-xs italic tracking-wide`;

const CAPS_LABEL_CLASS_NAME = tw`font-semibold text-content-icon text-xs uppercase tracking-wider`;
const CAPS_HINT_CLASS_NAME = tw`font-normal text-content-icon text-xs normal-case tracking-normal`;

// 'caps' renders a small uppercase label with the help text inline after it, on one line.
export type FieldLabelVariant = 'default' | 'caps';

/** The ids a labelled input and its description elements use, derived from the field name. */
export const fieldIds = (name: string) => ({
  inputId: `${name}-input`,
  helpTextId: `${name}-help-text`,
  errorId: `${name}-error`,
});

/**
 * The classes of the column that stacks a field's label, control and error. Pass the result to
 * `twx`, along with any extra classes.
 */
export const formFieldClassName = (variant: FieldLabelVariant): ClassName => [
  tw`flex flex-col`,
  variant === 'caps' ? tw`gap-1` : tw`gap-1.5`,
];

export interface FieldLabelProps {
  // Omitted renders the label as a <span>, for a control that carries its own accessible name,
  // e.g. a toggle.
  htmlFor?: string;
  label: string;
  variant?: FieldLabelVariant;
  // Keeps the label for assistive technology but hides it visually.
  hideLabel?: boolean;
  required?: boolean;
  helpText?: string;
  helpTextId?: string;
  // Classes for the label text.
  labelClassName?: ClassName;
}

interface LabelElementProps extends PropsWithChildren {
  htmlFor?: string;
  classes: ClassName;
}

const LabelElement = ({ htmlFor, classes, children }: LabelElementProps) =>
  htmlFor === undefined ? (
    <span className={twx(classes)}>{children}</span>
  ) : (
    <label htmlFor={htmlFor} className={twx(classes)}>
      {children}
    </label>
  );

/** The label, required mark and help text shown above a form input. */
export const FieldLabel = (props: FieldLabelProps) =>
  props.variant === 'caps' ? <CapsFieldLabel {...props} /> : <DefaultFieldLabel {...props} />;

const DefaultFieldLabel = ({
  htmlFor,
  label,
  hideLabel = false,
  required = false,
  helpText,
  helpTextId,
  labelClassName,
}: FieldLabelProps) => (
  <div className={twx(LABEL_CONTAINER_CLASS_NAME, { 'sr-only': hideLabel && !helpText })}>
    <LabelElement htmlFor={htmlFor} classes={[LABEL_CLASS_NAME, { 'sr-only': hideLabel }]}>
      <span className={twx(labelClassName)}>{label}</span>
      {required && (
        <span>
          <span className={twx(REQUIRED_MARK_CLASS_NAME)} aria-hidden="true">
            *
          </span>
          <span className={tw`sr-only`}>Required</span>
        </span>
      )}
    </LabelElement>
    {helpText && (
      <div id={helpTextId} className={tw`mt-1 flex items-center gap-0.5`}>
        <div className={twx(HELP_TEXT_CLASS_NAME)}>{helpText}</div>
      </div>
    )}
  </div>
);

const CapsFieldLabel = ({
  htmlFor,
  label,
  hideLabel = false,
  required = false,
  helpText,
  helpTextId,
  labelClassName,
}: FieldLabelProps) => (
  <div
    className={twx('flex min-w-0 items-baseline gap-1 leading-4', {
      'sr-only': hideLabel && !helpText,
    })}
  >
    <LabelElement
      htmlFor={htmlFor}
      classes={[CAPS_LABEL_CLASS_NAME, tw`shrink-0`, { 'sr-only': hideLabel }, labelClassName]}
    >
      {label}
      {required && (
        <>
          <span className={tw`ml-0.5 text-error-text`} aria-hidden="true">
            *
          </span>
          <span className={tw`sr-only`}> Required</span>
        </>
      )}
    </LabelElement>
    {helpText && (
      <span
        id={helpTextId}
        className={twx(CAPS_HINT_CLASS_NAME, 'min-w-0 truncate')}
        title={helpText}
      >
        {!hideLabel && <span aria-hidden="true">· </span>}
        {helpText}
      </span>
    )}
  </div>
);

export interface FieldErrorProps {
  id: string;
  error?: string;
}

/** The error message shown below a form input. Renders nothing without an error. */
export const FieldError = ({ id, error }: FieldErrorProps) =>
  error ? (
    <p id={id} className={tw`text-error-text text-xs`}>
      {error}
    </p>
  ) : null;

/** Joins the ids of the elements that describe an input, for `aria-describedby`. */
export const describedBy = (...ids: Array<string | false | undefined>) =>
  ids.filter(Boolean).join(' ') || undefined;

export interface FormFieldControlState {
  error?: string;
  disabled?: boolean;
}

/**
 * The border, focus ring and disabled classes of a bordered form control. Pass the result to
 * `twx`, along with any extra classes.
 */
export const formFieldControlClassName = ({
  error,
  disabled,
}: FormFieldControlState): ClassName => [
  tw`w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-1`,
  {
    'border-border-main focus:border-primary-main focus:ring-primary-main': !error,
    'border-border-error focus:border-border-error focus:ring-border-error': !!error,
    'cursor-not-allowed bg-background-disabled text-content-disabled': disabled,
  },
];
